import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Col, Form, Row, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import Api from "../Auth/Api";
import * as XLSX from "xlsx";
import { decodeJWT } from "../Components/jwtUtils";
import "./InformacoesUsuario.scss";

const limitarTexto = (texto, limite) => {
	if (!texto) return "";
	return texto.length > limite ? texto.substring(0, limite) + "..." : texto;
};

/** Datas no padrão dd/mm/aaaa …; se comHora for false, remove hora (demais níveis); admin vê data+hora. */
const dataParaViewer = (str, comHora) => {
	if (str == null || str === "") return "";
	const s = String(str).trim();
	if (comHora) return s;
	const m = s.match(/^(\d{2}\/\d{2}\/\d{4})(\s+.+)?$/);
	return m ? m[1] : s;
};

const timelineIcon = (tipo) => {
	const map = {
		aula_concluida: "book",
		avaliacao: "clipboard-check",
		avaliacao_reacao: "emoji-smile",
		pratica: "hammer",
		certificado: "award",
		aula_grupo: "people-fill",
		login: "box-arrow-in-right",
		log: "journal-text",
	};
	return map[tipo] || "circle-fill";
};

const tipoTimelineLabel = (tipo) => {
	const map = {
		aula_concluida: "Aula",
		avaliacao: "Avaliação",
		avaliacao_reacao: "Reação",
		pratica: "Prática",
		certificado: "Certificado",
		aula_grupo: "Aula em grupo",
		login: "Login",
		log: "Log",
	};
	return map[tipo] || tipo;
};

function safeJsonParse(raw, fallback = null) {
	if (raw == null || raw === "") return fallback;
	if (typeof raw === "object") return raw;
	try {
		return JSON.parse(raw);
	} catch {
		return fallback;
	}
}

function appendSheet(workbook, name, rows) {
	if (!rows || rows.length === 0) return;
	const safe = String(name).substring(0, 31);
	XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), safe);
}

function rowsCertificadosExcel(certificados) {
	return (certificados || []).map((c) => ({
		curso: c.nome_curso,
		data_emissao: c.data_emissao,
		situacao: c.situacao_certificado,
		detalhe: c.situacao_detalhe || "",
	}));
}

function filtroAulaGrupoParticipou(g) {
	if (typeof g.foi_participante === "boolean") return g.foi_participante;
	return String(g.papel_grupo || "").toLowerCase().includes("participante");
}

function filtroAulaGrupoMinistrou(g) {
	if (typeof g.foi_ministrante === "boolean") return g.foi_ministrante;
	return String(g.papel_grupo || "").toLowerCase().includes("ministrou");
}

function InformacoesUsuario() {
	const { id } = useParams();
	const navigate = useNavigate();
	const token = JSON.parse(localStorage.getItem("user_token"));
	const viewerPayload = typeof token === "string" ? decodeJWT(token) : null;
	const viewerIsAdmin = String(viewerPayload?.nivel || "").trim().toLowerCase() === "administrador";

	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [activeTab, setActiveTab] = useState(null);
	const [logFilter, setLogFilter] = useState("");
	const [expandedReacaoRow, setExpandedReacaoRow] = useState({});
	const [aulasGrupoModo, setAulasGrupoModo] = useState("participou");

	useEffect(() => {
		setAulasGrupoModo("participou");
	}, [id]);

	useEffect(() => {
		setActiveTab(null);
	}, [id]);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await Api.get(`/api/usuario/informacoes/${id}`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			setData(response.data);
		} catch (e) {
			const msg = e.response?.data?.message || e.message || "Erro ao carregar";
			setError(msg);
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [id, token]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const usuario = data?.usuario;
	const stats = data?.estatisticas || {};
	const isAluno = String(usuario?.nivel || "").trim().toLowerCase() === "aluno";
	const isGestorPerfil = String(usuario?.nivel || "").trim().toLowerCase() === "gestor";
	const isRhPerfil = String(usuario?.nivel || "").trim().toLowerCase() === "rh";
	const isAdminPerfil = String(usuario?.nivel || "").trim().toLowerCase() === "administrador";
	const exibirPainelTreino = isAluno || isGestorPerfil;
	const exibirAulasGrupo = exibirPainelTreino || isRhPerfil || isAdminPerfil;
	const alunosResponsaveis = data?.alunos_responsaveis || [];

	const primeiraAbaConteudo = useMemo(() => {
		if (isGestorPerfil && alunosResponsaveis.length > 0) return "alunos_gestor";
		if (exibirPainelTreino && !isGestorPerfil) return "cursos";
		if (exibirPainelTreino) return "aulas";
		if (exibirAulasGrupo) return "grupo";
		return "aulas";
	}, [isGestorPerfil, alunosResponsaveis.length, exibirPainelTreino, exibirAulasGrupo]);

	const tabKey = activeTab ?? (viewerIsAdmin ? "timeline" : primeiraAbaConteudo);

	useEffect(() => {
		if (viewerIsAdmin || !activeTab) return;
		if (activeTab === "timeline" || activeTab === "logs") setActiveTab(null);
	}, [viewerIsAdmin, activeTab]);

	const aulasGrupoParticipouLista = useMemo(
		() => (data?.aulas_grupo || []).filter(filtroAulaGrupoParticipou),
		[data?.aulas_grupo]
	);
	const aulasGrupoMinistrouLista = useMemo(
		() => (data?.aulas_grupo || []).filter(filtroAulaGrupoMinistrou),
		[data?.aulas_grupo]
	);
	const aulasGrupoVisivel =
		aulasGrupoModo === "ministrou" ? aulasGrupoMinistrouLista : aulasGrupoParticipouLista;

	const filteredLogs = useMemo(() => {
		const logs = data?.logs || [];
		const q = logFilter.trim().toLowerCase();
		if (!q) return logs;
		return logs.filter(
			(l) =>
				String(l.acao || "").toLowerCase().includes(q) ||
				String(l.data || "").toLowerCase().includes(q) ||
				String(l.ip || "").toLowerCase().includes(q)
		);
	}, [data?.logs, logFilter]);

	const timelineGrouped = useMemo(() => {
		const timeline = data?.timeline || [];
		const groups = {};
		timeline.forEach((ev) => {
			const day = String(ev.data || "").split(" ")[0] || "—";
			if (!groups[day]) groups[day] = [];
			groups[day].push(ev);
		});
		return groups;
	}, [data?.timeline]);

	const exportRowsUsuario = () =>
		usuario
			? [
					{
						ID: usuario.id,
						Nome: usuario.nome_completo,
						Matrícula: usuario.codigo,
						Email: usuario.email,
						CPF: usuario.cpf || "",
						Celular: usuario.celular || "",
						Unidade: usuario.unidade,
						Cargo: usuario.cargo || "",
						Nível: usuario.nivel,
						Status: usuario.status,
						Gestor: usuario.gestor_nome || "",
						Favorito: usuario.favorito ? "Sim" : "Não",
					},
			  ]
			: [];

	const exportRowsStats = () =>
		stats
			? [
					{
						total_aulas_vinculadas: stats.total_aulas_vinculadas,
						total_aulas_concluidas: stats.total_aulas_concluidas,
						perc_conclusao_geral: stats.perc_conclusao_geral,
						total_avaliacoes_aprovadas: stats.total_avaliacoes_aprovadas,
						total_certificados_ativos: stats.total_certificados_ativos,
						total_certificados_expirados: stats.total_certificados_expirados,
						total_praticas: stats.total_praticas,
						total_aulas_em_grupo: stats.total_aulas_em_grupo,
						ultima_atividade: stats.ultima_atividade,
						dias_desde_ultimo_login: stats.dias_desde_ultimo_login,
					},
			  ]
			: [];

	const handleExportSection = (section) => {
		if (!data) return;
		const wb = XLSX.utils.book_new();
		const codigo = usuario?.codigo || id;

		if (section === "timeline") {
			appendSheet(
				wb,
				"Timeline",
				(data.timeline || []).map((e) => ({
					Tipo: tipoTimelineLabel(e.tipo),
					Data: e.data,
					Descrição: e.descricao,
					Arquivado: e.arquivado ?? "",
				}))
			);
		}
		if (section === "cursos") appendSheet(wb, "Cursos", data.cursos_vinculados || []);
		if (section === "aulas") appendSheet(wb, "Aulas", data.aulas_concluidas || []);
		if (section === "avaliacoes") appendSheet(wb, "Avaliações", data.avaliacoes_concluidas || []);
		if (section === "reacoes") {
			const rows = (data.avaliacoes_reacao || []).map((a) => ({
				tipo: a.tipo,
				curso: a.nome_curso,
				modulo: a.nome_modulo,
				data: a.data_formatada,
				arquivado: a.arquivado_label,
				comentarios: a.comentarios || "",
			}));
			appendSheet(wb, "Reações", rows);
		}
		if (section === "praticas") appendSheet(wb, "Práticas", data.praticas || []);
		if (section === "grupo") appendSheet(wb, "Aulas_grupo", data.aulas_grupo || []);
		if (section === "certificados") appendSheet(wb, "Certificados", rowsCertificadosExcel(data.certificados));
		if (section === "logs")
			appendSheet(
				wb,
				"Logs",
				(data.logs || []).map((l) => ({ acao: l.acao, data: l.data, ip: l.ip }))
			);
		if (section === "alunos_gestor") appendSheet(wb, "Alunos_gestor", data.alunos_responsaveis || []);

		if (!wb.SheetNames?.length) {
			XLSX.utils.book_append_sheet(
				wb,
				XLSX.utils.json_to_sheet([{ mensagem: "Sem dados nesta seção" }]),
				"info"
			);
		}

		XLSX.writeFile(wb, `informacoes_${section}_${codigo}.xlsx`);
	};

	const handleExportFull = () => {
		if (!data) return;
		const wb = XLSX.utils.book_new();
		const codigo = usuario?.codigo || id;

		appendSheet(wb, "Usuario", exportRowsUsuario());
		appendSheet(wb, "Estatísticas", exportRowsStats());
		appendSheet(
			wb,
			"Timeline",
			(data.timeline || []).map((e) => ({
				Tipo: tipoTimelineLabel(e.tipo),
				Data: e.data,
				Descrição: e.descricao,
				Arquivado: e.arquivado ?? "",
			}))
		);
		appendSheet(wb, "Cursos", data.cursos_vinculados || []);
		appendSheet(wb, "Aulas_concluidas", data.aulas_concluidas || []);
		appendSheet(wb, "Avaliacoes", data.avaliacoes_concluidas || []);
		appendSheet(
			wb,
			"Avaliacoes_reacao",
			(data.avaliacoes_reacao || []).map((a) => ({
				tipo: a.tipo,
				curso: a.nome_curso,
				modulo: a.nome_modulo,
				data: a.data_formatada,
				arquivado: a.arquivado_label,
				comentarios: a.comentarios || "",
			}))
		);
		appendSheet(wb, "Praticas", data.praticas || []);
		appendSheet(wb, "Aulas_grupo", data.aulas_grupo || []);
		appendSheet(wb, "Certificados", rowsCertificadosExcel(data.certificados));
		appendSheet(
			wb,
			"Logs",
			(data.logs || []).map((l) => ({ acao: l.acao, data: l.data, ip: l.ip }))
		);
		appendSheet(wb, "Alunos_responsaveis", data.alunos_responsaveis || []);

		XLSX.writeFile(wb, `informacoes_completo_${codigo}.xlsx`);
	};

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center py-5">
				<Spinner animation="border" variant="primary" />
			</div>
		);
	}

	if (error || !usuario) {
		return (
			<div className="fade-in w-100 mt-3 px-2">
				<Button variant="outline-secondary" className="mb-3" onClick={() => navigate("/listar-usuarios")}>
					<i className="bi bi-arrow-left me-1"></i> Voltar
				</Button>
				<div className="alert alert-danger">{error || "Usuário não encontrado"}</div>
			</div>
		);
	}

	const statCards = [
		isGestorPerfil
			? {
					icon: "bi-journal-bookmark",
					label: "Aulas concluídas",
					value: String(stats.total_aulas_concluidas ?? 0),
			  }
			: {
					icon: "bi-journal-bookmark",
					label: "Aulas (concl. / vinc.)",
					value: `${stats.total_aulas_concluidas ?? 0} / ${stats.total_aulas_vinculadas ?? 0}`,
					sub: `Conclusão geral: ${stats.perc_conclusao_geral ?? 0}%`,
			  },
		{
			icon: "bi-award",
			label: "Certificados",
			value: `${stats.total_certificados_ativos ?? 0} / ${stats.total_certificados_expirados ?? 0}`,
			sub: "Ativos / expirados",
		},
		{
			icon: "bi-clipboard-check",
			label: "Avaliações aprovadas",
			value: stats.total_avaliacoes_aprovadas ?? 0,
			sub: "Nota ≥ 7",
		},
		{
			icon: "bi-tools",
			label: "Práticas ativas",
			value: stats.total_praticas ?? 0,
		},
		{
			icon: "bi-people",
			label: "Aulas em grupo",
			value: stats.total_aulas_em_grupo ?? 0,
		},
		{
			icon: "bi-clock-history",
			label: "Última atividade",
			value: stats.ultima_atividade
				? limitarTexto(dataParaViewer(String(stats.ultima_atividade), viewerIsAdmin), 28)
				: "—",
			titleValue: stats.ultima_atividade
				? dataParaViewer(String(stats.ultima_atividade), viewerIsAdmin)
				: undefined,
			sub:
				stats.dias_desde_ultimo_login != null
					? `Último login há ${stats.dias_desde_ultimo_login} dia(s)`
					: "",
		},
	];

	return (
		<div className="fade-in w-100 mt-3 pb-5 px-2">
			<div className="d-flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
				<Button variant="outline-secondary" onClick={() => navigate("/listar-usuarios")}>
					<i className="bi bi-arrow-left me-1"></i> Lista de usuários
				</Button>
				<Button variant="secondary" onClick={handleExportFull}>
					<i className="bi bi-table me-1"></i> Excel completo
				</Button>
			</div>

			<Card className="border-0 shadow-sm mb-4">
				<Card.Body className="d-flex flex-column flex-md-row gap-4 align-items-start">
					<div className="picture text-white bg-secondary rounded-circle overflow-hidden flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: 96, height: 96 }}>
						{usuario.foto ? (
							<img
								src={`${process.env.REACT_APP_API_URL}/fotos/${usuario.foto}`}
								alt=""
								className="object-fit-cover w-100 h-100"
							/>
						) : (
							<i className="bi bi-person fs-1"></i>
						)}
					</div>
					<div className="flex-grow-1">
						<h3 className="text-primary fw-bold mb-1">{usuario.nome_completo}</h3>
						<div className="d-flex flex-wrap gap-2 mb-2">
							<Badge bg="primary">{usuario.nivel}</Badge>
							<Badge bg={usuario.status === "ativo" ? "success" : "danger"}>
								{usuario.status === "ativo" ? "Ativo" : "Inativo"}
							</Badge>
							{usuario.favorito && <Badge bg="warning text-dark">Favorito</Badge>}
						</div>
						<Row className="g-2 small">
							<Col sm={6}>
								<strong>Matrícula:</strong> {usuario.codigo}
							</Col>
							<Col sm={6}>
								<strong>E-mail:</strong> {usuario.email}
							</Col>
							<Col sm={6}>
								<strong>Unidade:</strong> {usuario.unidade}
							</Col>
							<Col sm={6}>
								<strong>Cargo:</strong> {usuario.cargo || "—"}
							</Col>
							{usuario.cpf && (
								<Col sm={6}>
									<strong>CPF:</strong> {usuario.cpf}
								</Col>
							)}
							{usuario.celular && (
								<Col sm={6}>
									<strong>Celular:</strong> {usuario.celular}
								</Col>
							)}
							{isAluno && (
								<Col xs={12}>
									<strong>Gestor responsável:</strong>{" "}
									{usuario.gestor_nome || <span className="text-muted">Não consta</span>}
								</Col>
							)}
						</Row>
					</div>
				</Card.Body>
			</Card>

			{exibirPainelTreino && (
				<Row className="g-3 mb-4 informacoes-usuario-stats">
					{statCards.map((c) => (
						<Col key={c.label} xs={12} sm={6} xl={4}>
							<Card className="informacoes-usuario-stat-card border-0 shadow-sm h-100">
								<Card.Body className="d-flex gap-3 align-items-start p-3 p-sm-4">
									<div className="informacoes-usuario-stat-icon flex-shrink-0" aria-hidden>
										<i className={`bi ${c.icon}`} />
									</div>
									<div className="flex-grow-1 min-w-0">
										<div className="informacoes-usuario-stat-label text-uppercase">{c.label}</div>
										<div
											className="informacoes-usuario-stat-value"
											title={c.titleValue || undefined}
										>
											{c.value}
										</div>
										{c.sub ? (
											<div className="informacoes-usuario-stat-sub">
												<span className="align-middle">{c.sub}</span>
											</div>
										) : null}
									</div>
								</Card.Body>
							</Card>
						</Col>
					))}
				</Row>
			)}

			<div className="informacoes-usuario-sheet mb-4">
				<Tabs
					activeKey={tabKey}
					onSelect={(k) => setActiveTab(k || (viewerIsAdmin ? "timeline" : primeiraAbaConteudo))}
					className="informacoes-usuario-tabs-root mb-0"
				>
				{viewerIsAdmin && (
				<Tab eventKey="timeline" title="Timeline">
					<div className="d-flex justify-content-end mb-2">
						<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("timeline")}>
							<i className="bi bi-download me-1"></i> Excel desta seção
						</Button>
					</div>
					{Object.keys(timelineGrouped).length === 0 ? (
						<p className="text-muted">Nenhum evento na linha do tempo.</p>
					) : (
						Object.entries(timelineGrouped).map(([dia, evs]) => (
							<div key={dia} className="mb-4">
								<h6 className="text-primary border-bottom pb-1">{dia}</h6>
								<ul className="list-unstyled mb-0">
									{evs.map((ev, i) => (
										<li key={`${dia}-${i}`} className="d-flex gap-3 py-2 border-bottom">
											<div className="text-primary pt-1">
												<i className={`bi bi-${timelineIcon(ev.tipo)}`}></i>
											</div>
											<div className="flex-grow-1">
												<div className="d-flex flex-wrap gap-2 align-items-center">
													<Badge bg="light" text="dark">
														{tipoTimelineLabel(ev.tipo)}
													</Badge>
													<span className="text-muted small">{ev.data}</span>
													{ev.arquivado && (
														<Badge bg={ev.arquivado === "Sim" ? "danger" : "success"}>
															Arq: {ev.arquivado}
														</Badge>
													)}
												</div>
												<div className="small mt-1">{ev.descricao}</div>
											</div>
										</li>
									))}
								</ul>
							</div>
						))
					)}
				</Tab>
				)}

				{isGestorPerfil && (
					<Tab
						eventKey="alunos_gestor"
						title={`Alunos sob responsabilidade (${alunosResponsaveis.length})`}
					>
						<div className="d-flex justify-content-end mb-2">
							<Button
								size="sm"
								variant="outline-secondary"
								onClick={() => handleExportSection("alunos_gestor")}
							>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						{alunosResponsaveis.length === 0 ? (
							<p className="text-muted mb-0">
								Nenhum aluno com este usuário como gestor responsável no cadastro.
							</p>
						) : (
							<Table responsive striped hover size="sm">
								<thead>
									<tr>
										<th>Nome</th>
										<th>Matrícula</th>
										<th>E-mail</th>
										<th>Unidade</th>
										<th>Status</th>
										<th className="text-end text-nowrap">Ações</th>
									</tr>
								</thead>
								<tbody>
									{alunosResponsaveis.map((al) => (
										<tr key={al.id}>
											<td>{al.nome_completo}</td>
											<td>{al.codigo}</td>
											<td>{al.email}</td>
											<td>{al.unidade}</td>
											<td>{al.status}</td>
											<td className="text-end">
												<Button
													size="sm"
													variant="outline-primary"
													onClick={() => navigate(`/usuarios/informacoes/${al.id}`)}
												>
													<i className="bi bi-person-lines-fill me-1"></i>
													Informações
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
					</Tab>
				)}

				{exibirPainelTreino && !isGestorPerfil && (
					<Tab eventKey="cursos" title="Cursos vinculados">
						<div className="d-flex justify-content-end mb-2 gap-2">
							<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("cursos")}>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Curso</th>
									<th>Tipo</th>
									<th className="text-center">Módulos</th>
									<th className="text-center">Aulas</th>
									<th className="text-center">Concluídas</th>
									<th className="text-center">%</th>
								</tr>
							</thead>
							<tbody>
								{(data.cursos_vinculados || []).map((c) => (
									<tr key={c.id_curso}>
										<td>{c.nome_curso}</td>
										<td>{c.tipo_treinamento}</td>
										<td className="text-center">{c.qtd_modulos}</td>
										<td className="text-center">{c.qtd_aulas}</td>
										<td className="text-center">{c.aulas_concluidas}</td>
										<td className="text-center">{c.perc_conclusao_aulas}%</td>
									</tr>
								))}
							</tbody>
						</Table>
						{!(data.cursos_vinculados || []).length && (
							<p className="text-muted">Sem cursos em permissão.</p>
						)}
					</Tab>
				)}

				{exibirPainelTreino && (
					<Tab eventKey="aulas" title="Aulas concluídas">
						<div className="d-flex justify-content-end mb-2">
							<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("aulas")}>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Curso</th>
									<th>Módulo</th>
									<th>Aula</th>
									<th>{viewerIsAdmin ? "Data/hora" : "Data"}</th>
									<th>Arquivada</th>
								</tr>
							</thead>
							<tbody>
								{(data.aulas_concluidas || []).map((a, i) => (
									<tr key={i}>
										<td>{a.nome_curso}</td>
										<td>{a.nome_modulo}</td>
										<td>{a.titulo_aula}</td>
										<td>{dataParaViewer(a.datetime, viewerIsAdmin)}</td>
										<td>{a.arquivado_label}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Tab>
				)}

				{exibirPainelTreino && (
					<Tab eventKey="avaliacoes" title="Avaliações">
						<div className="d-flex justify-content-end mb-2">
							<Button
								size="sm"
								variant="outline-secondary"
								onClick={() => handleExportSection("avaliacoes")}
							>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Curso</th>
									<th>Módulo</th>
									<th>Nota</th>
									<th>Data</th>
									<th>Arquivada</th>
								</tr>
							</thead>
							<tbody>
								{(data.avaliacoes_concluidas || []).map((a) => (
									<tr key={a.id}>
										<td>{a.nome_curso}</td>
										<td>{a.nome_modulo}</td>
										<td>{a.nota}</td>
										<td>{dataParaViewer(a.data, viewerIsAdmin)}</td>
										<td>{a.arquivado_label}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Tab>
				)}

				{exibirPainelTreino && (
					<Tab eventKey="reacoes" title="Avaliações de reação">
						<div className="d-flex justify-content-end mb-2">
							<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("reacoes")}>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Tipo</th>
									<th>Curso</th>
									<th>Módulo</th>
									<th>Data</th>
									<th>Arquivada</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{(data.avaliacoes_reacao || []).map((a) => (
									<React.Fragment key={a.id}>
										<tr>
											<td>{a.tipo}</td>
											<td>{a.nome_curso}</td>
											<td>{a.nome_modulo}</td>
											<td>{dataParaViewer(a.data_formatada, viewerIsAdmin)}</td>
											<td>{a.arquivado_label}</td>
											<td>
												<Button
													size="sm"
													variant="outline-primary"
													onClick={() =>
														setExpandedReacaoRow((p) => ({
															...p,
															[a.id]: !p[a.id],
														}))
													}
												>
													{expandedReacaoRow[a.id] ? "Ocultar" : "Detalhes"}
												</Button>
											</td>
										</tr>
										{expandedReacaoRow[a.id] && (
											<tr>
												<td colSpan={6}>
													{(() => {
														let questoes = safeJsonParse(a.questoes, []);
														let respostas = safeJsonParse(a.respostas, []);
														return (
															<div className="p-3 bg-light rounded">
																<Table size="sm" responsive striped>
																	<tbody>
																		{questoes &&
																			respostas &&
																			questoes.map((q, qi) => (
																				<tr key={qi}>
																					<td>{q}</td>
																					<td>{respostas[qi] ?? "—"}</td>
																				</tr>
																			))}
																	</tbody>
																</Table>
																{a.comentarios && <p className="mb-0">{a.comentarios}</p>}
															</div>
														);
													})()}
												</td>
											</tr>
										)}
									</React.Fragment>
								))}
							</tbody>
						</Table>
					</Tab>
				)}

				{exibirPainelTreino && (
					<Tab eventKey="praticas" title="Práticas">
						<div className="d-flex justify-content-end mb-2">
							<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("praticas")}>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Curso</th>
									<th>Data</th>
									<th>Local</th>
									<th>Gestor</th>
									<th>Status</th>
								</tr>
							</thead>
							<tbody>
								{(data.praticas || []).map((p) => (
									<tr key={p.id}>
										<td>{p.nome_curso}</td>
										<td>{dataParaViewer(p.data_pratica_formatada, viewerIsAdmin)}</td>
										<td>{p.local}</td>
										<td>{p.gestor}</td>
										<td>{p.arquivado_label}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Tab>
				)}

				{exibirAulasGrupo && (
					<Tab eventKey="grupo" title="Aulas em grupo">
						<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
							<div className="btn-group shadow-sm" role="group" aria-label="Aulas em grupo por papel">
								<Button
									type="button"
									size="sm"
									variant={aulasGrupoModo === "participou" ? "primary" : "outline-primary"}
									className="px-3"
									onClick={() => setAulasGrupoModo("participou")}
								>
									<i className="bi bi-people me-1" aria-hidden></i>
									Participou ({aulasGrupoParticipouLista.length})
								</Button>
								<Button
									type="button"
									size="sm"
									variant={aulasGrupoModo === "ministrou" ? "primary" : "outline-primary"}
									className="px-3"
									onClick={() => setAulasGrupoModo("ministrou")}
								>
									<i className="bi bi-easel me-1" aria-hidden></i>
									Ministrou ({aulasGrupoMinistrouLista.length})
								</Button>
							</div>
							<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("grupo")}>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						{!(data.aulas_grupo || []).length ? (
							<p className="text-muted mb-0">Sem registros de aula em grupo.</p>
						) : aulasGrupoVisivel.length === 0 ? (
							<p className="text-muted mb-0">
								{aulasGrupoModo === "ministrou"
									? "Nenhuma aula em grupo com este usuário como gestor aplicador (ministrou)."
									: "Nenhuma aula em grupo com este usuário na lista de participantes."}
							</p>
						) : (
							<Table responsive striped hover size="sm">
								<thead>
									<tr>
										<th>Data</th>
										<th>Aula</th>
										<th>Módulo</th>
										<th>Curso</th>
										<th>Gestor aplicador</th>
									</tr>
								</thead>
								<tbody>
									{aulasGrupoVisivel.map((g) => (
										<tr key={`${g.id_lista}-${aulasGrupoModo}`}>
											<td>{dataParaViewer(g.data, viewerIsAdmin)}</td>
											<td>{g.nome_aula}</td>
											<td>{g.nome_modulo}</td>
											<td>{g.nome_curso}</td>
											<td>{g.nome_gestor}</td>
										</tr>
									))}
								</tbody>
							</Table>
						)}
					</Tab>
				)}

				{exibirPainelTreino && (
					<Tab eventKey="certificados" title="Certificados">
						<div className="d-flex justify-content-end mb-2">
							<Button
								size="sm"
								variant="outline-secondary"
								onClick={() => handleExportSection("certificados")}
							>
								<i className="bi bi-download me-1"></i> Excel desta seção
							</Button>
						</div>
						<Table responsive striped hover size="sm">
							<thead>
								<tr>
									<th>Curso</th>
									<th>Data de emissão</th>
									<th style={{ minWidth: 220 }}>
										Situação
									</th>
								</tr>
							</thead>
							<tbody>
								{(data.certificados || []).map((c) => (
									<tr key={c.id}>
										<td>{c.nome_curso}</td>
										<td>{dataParaViewer(c.data_emissao, viewerIsAdmin)}</td>
										<td>
											{/* Situação pela data: o certificado arquivado na reciclagem vale até vencer */}
											<Badge bg={c.situacao_certificado === "Expirado" ? "danger" : "success"} className="me-1">
												{c.situacao_certificado}
											</Badge>
											{c.situacao_detalhe && <small className="text-muted">{c.situacao_detalhe}</small>}
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Tab>
				)}

				{viewerIsAdmin && (
				<Tab eventKey="logs" title="Logs">
					<div className="d-flex flex-wrap gap-2 justify-content-between mb-2">
						<Form.Control
							style={{ maxWidth: 320 }}
							size="sm"
							placeholder="Filtrar logs..."
							value={logFilter}
							onChange={(e) => setLogFilter(e.target.value)}
						/>
						<Button size="sm" variant="outline-secondary" onClick={() => handleExportSection("logs")}>
							<i className="bi bi-download me-1"></i> Excel desta seção
						</Button>
					</div>
					<div className="table-responsive" style={{ maxHeight: 520, overflowY: "auto" }}>
						<Table striped hover size="sm">
							<thead className="sticky-top bg-white">
								<tr>
									<th>Data</th>
									<th>Ação</th>
									<th>IP</th>
								</tr>
							</thead>
							<tbody>
								{filteredLogs.map((l) => (
									<tr key={l.id}>
										<td style={{ whiteSpace: "nowrap" }}>{l.data}</td>
										<td className="small">{l.acao}</td>
										<td>{l.ip}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</div>
				</Tab>
				)}
				</Tabs>
			</div>
		</div>
	);
}

export default InformacoesUsuario;
